import { FormApi } from "final-form";
import _ from "lodash";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { capitalizeFirstLetter } from "src/ui/utils/string";
import { makeStyles } from "tss-react/mui";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { AppPermission, Provisioning } from "@dashboard/provisioning/types";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { optionsAction } from "../utils";

const initialValues: Partial<AppPermission> = {
    package: undefined,
    permission: undefined,
    action: "Grant",
    comment: null,
};
const useStyles = makeStyles()((theme) => ({
    input: { flex: 1 },
    box: { display: "inline-flex", flex: 1, gap: theme.spacing(2) },
}));

type FormValues = AppPermission;
type ValidationErros = Omit<FormValues, "action"> & { action: string };

export const RuntimePermissions = () => {
    const { value: initialValue, onChange } = useCollapsiblePersistence(
        "provisioningPage.collapsedSections",
        "RuntimePermissions",
    );
    const { classes } = useStyles();
    const parentForm = useForm();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentValues } = useFormState<Partial<Provisioning>>();
    const permissions = parentValues?.settings?.appPermissions || [];

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const newValue = { ...values, comment: values.comment || null };
        if (permissions.length) {
            parentForm.change("settings.appPermissions", [...permissions, newValue]);
        }
        if (!permissions.length) {
            parentForm.change("settings.appPermissions", [newValue]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        return _.transform<FormValues, ValidationErros>(values, (result, value, key) => {
            if (!value && key !== "comment") {
                result[key] = t("pages:provisioningPage.input.comment.validation.empty", {
                    component: capitalizeFirstLetter(key),
                });
            }
        });
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.appPermissions",
            permissions.filter((_item, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={initialValue} onChange={onChange}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>
                            {t("pages:provisioningPage.section.runtimePermissions.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <SectionDescription>
                            {t("pages:provisioningPage.section.runtimePermissions.description")}
                        </SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.package")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.permission")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.action")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.comment")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {permissions.length ? (
                                        permissions.map((item, index) => (
                                            <MinimalTableRow key={item.package + item.permission}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <Typography>{item.package}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.permission}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.action}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.comment}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => handleDelete(index)}
                                                        name="runtime-permission-delete"
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={5}>
                                                <Typography>{t("pages:provisioningPage.table.noRuntimePermissions")}</Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        <Row style={{ marginTop: theme.spacing(4), paddingBottom: theme.spacing(3) }}>
                            <TextField
                                name="package"
                                label={t("pages:provisioningPage.input.package.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <TextField
                                name="permission"
                                label={t("pages:provisioningPage.input.permission.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row>
                            <ControlledSelectField
                                name="action"
                                label={t("pages:provisioningPage.input.action.caption")}
                                className={classes.input}
                                disabled={!provisionings.length}
                                options={optionsAction}
                            />
                            <div className={classes.box}>
                                <TextField
                                    name="comment"
                                    label={t("common:label.comment")}
                                    className={classes.input}
                                    disabled={!provisionings.length}
                                />
                                <PrimaryOutlinedButton
                                    onClick={form.submit}
                                    disabled={!provisionings.length}
                                    style={{ height: 40 }}
                                    name="runtime-permission-add"
                                >
                                    {t("common:button.add")}
                                </PrimaryOutlinedButton>
                            </div>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
